import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp, Wand2, Loader2 } from 'lucide-react';
import apiService from '../services/apiService';

// Field components defined OUTSIDE to prevent re-creation on every render
const Field = ({ label, field, type = 'text', options = null, width = 'full', placeholder = '', value, onChange }) => (
  <div className={`${width === 'full' ? 'col-span-1' : width === 'half' ? 'col-span-1' : 'col-span-1'}`}>
    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
    {options ? (
      <select
        value={value || ''}
        onChange={onChange}
        className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
      >
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    ) : type === 'checkbox' ? (
      <input
        type="checkbox"
        checked={value || false}
        onChange={onChange}
        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
    ) : type === 'textarea' ? (
      <textarea
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
      />
    )}
  </div>
);

const CheckboxField = ({ label, field, checked, onChange }) => (
  <label className="flex items-center space-x-2">
    <input
      type="checkbox"
      checked={checked || false}
      onChange={onChange}
      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
    />
    <span className="text-sm text-gray-700">{label}</span>
  </label>
);

const UAD26FormComplete = ({ subjectProperty, selectedComparables, marketData, onUpdate }) => {
  const [formData, setFormData] = useState({
    // PAGE 1 - SUBJECT PROPERTY IDENTIFICATION
    propertyAddress: '',
    city: '',
    state: '',
    zip: '',
    borrower: '',
    owner: '',
    county: '',
    legalDescription: '',
    apn: '',
    taxYear: new Date().getFullYear(),
    reTaxes: '',
    neighborhoodName: '',
    mapReference: '',
    censusTract: '',
    occupant: 'Owner', // Owner, Tenant, Vacant
    specialAssessments: '',
    pud: false,
    hoa: '',
    hoaPer: 'month', // year or month
    propertyRights: 'Fee Simple', // Fee Simple, Leasehold, Other
    assignmentType: 'Purchase Transaction', // Purchase Transaction, Refinance Transaction, Other
    lenderClient: '',
    lenderAddress: '',

    // CONTRACT FOR SALE
    analyzeContract: true,
    contractAnalysisResults: '',
    contractPrice: '',
    contractDate: '',
    sellerOwner: true,
    dataSource: '',
    financialAssistance: false,
    financialAssistanceAmount: '',
    financialAssistanceDescription: '',

    // CURRENTLY OFFERED FOR SALE
    currentlyOffered: false,
    offeringDataSources: '',
    offeringPrice: '',
    offeringDate: '',

    // NEIGHBORHOOD DESCRIPTION
    location: 'Suburban', // Urban, Suburban, Rural
    builtUp: '25-75%', // Over 75%, 25-75%, Under 25%
    growth: 'Stable', // Rapid, Stable, Slow
    propertyValues: 'Stable', // Increasing, Stable, Declining
    demandSupply: 'In Balance', // Shortage, In Balance, Over Supply
    marketingTime: '3-6 mths', // Under 3 mths, 3-6 mths, Over 6 mths
    oneUnitPriceLow: '',
    oneUnitPriceHigh: '',
    oneUnitAgeLow: '',
    oneUnitAgeHigh: '',
    oneUnitPercent: '',
    twoFourUnitPercent: '',
    multiFamilyPercent: '',
    commercialPercent: '',
    otherPercent: '',
    neighborhoodBoundaries: '',
    neighborhoodDescription: '',
    marketConditions: '',

    // SITE
    dimensions: '',
    area: '',
    shape: '',
    view: '',
    zoningClassification: '',
    zoningDescription: '',
    zoningCompliance: 'Legal', // Legal, Legal Nonconforming, No Zoning, Illegal
    highestBestUse: true,
    highestBestUseDescription: '',
    adverseSiteConditions: false,
    adverseSiteDescription: '',

    // UTILITIES
    electricity: 'Public',
    electricityOther: '',
    gas: 'Public',
    gasOther: '',
    water: 'Public',
    waterOther: '',
    sanitarySewer: 'Public',
    sanitarySewerOther: '',
    streetType: 'Public',
    alley: 'None',

    // FEMA FLOOD
    femaFloodHazard: false,
    femaZone: '',
    femaMap: '',
    femaMapDate: '',
    utilitiesTypical: true,
    utilitiesDescription: '',

    // IMPROVEMENTS - GENERAL DESCRIPTION
    units: 'One', // One, One with Accessory Unit
    stories: '1',
    type: 'Det.', // Det., Att., S-Det./End Unit
    existing: true, // Existing, Proposed, Under Const.
    designStyle: '',
    yearBuilt: '',
    effectiveAge: '',

    // FOUNDATION
    foundation: 'Concrete Slab', // Concrete Slab, Crawl Space, Full Basement, Partial Basement
    basementArea: '',
    basementFinishPercent: '',
    outsideEntry: false,
    sumpPump: false,
    evidenceInfestation: false,
    dampness: false,
    settlement: false,

    // EXTERIOR DESCRIPTION
    foundationWalls: '',
    exteriorWalls: '',
    roofSurface: '',
    guttersDownspouts: '',
    windowType: '',
    stormSash: false,
    screens: false,

    // INTERIOR
    floors: '',
    walls: '',
    trimFinish: '',
    bathFloor: '',
    bathWainscot: '',

    // CAR STORAGE
    carStorage: 'None', // None, Driveway, Garage, Carport
    carStorageSpaces: '',
    driveway: false,
    drivewaySpaces: '',
    drivewaySurface: '',
    garage: false,
    garageSpaces: '',
    garageType: '', // Att., Det., Built-in
    carport: false,
    carportSpaces: '',
    carportType: '',

    // ATTIC
    atticType: 'None', // None, Drop Stair, Stairs, Floor, Scuttle, Finished, Heated

    // HEATING/COOLING
    heatingType: 'FWA', // FWA, HWBB, Radiant, Other
    heatingFuel: 'Gas',
    coolingType: 'Central Air Conditioning', // Central Air Conditioning, Individual, Other

    // AMENITIES
    fireplace: false,
    fireplaceNumber: '',
    woodstove: false,
    woodstoveNumber: '',
    patioDeck: false,
    pool: false,
    fence: false,
    porch: false,
    other: '',

    // APPLIANCES
    refrigerator: false,
    rangeOven: false,
    dishwasher: false,
    disposal: false,
    microwave: false,
    washerDryer: false,
    appliancesOther: '',

    // ROOM COUNT & SQUARE FOOTAGE
    totalRooms: '',
    bedrooms: '',
    bathrooms: '',
    squareFeetGLA: '',

    // ADDITIONAL FEATURES
    additionalFeatures: '',

    // CONDITION & DEFICIENCIES
    conditionDescription: '',
    physicalDeficiencies: false,
    physicalDeficienciesDescription: '',
    conformsToNeighborhood: true,
    conformityDescription: '',

    // PAGE 2 - SALES COMPARISON APPROACH
    comparableSalesRangeLow: '',
    comparableSalesRangeHigh: '',
    comparableListingsRangeLow: '',
    comparableListingsRangeHigh: '',

    // Prior sales research
    researchedPriorSales: true,
    subjectPriorSalesThreeYears: false,
    subjectPriorSalesDataSource: '',
    comparablePriorSalesOneYear: false,
    comparablePriorSalesDataSource: '',
    priorSaleAnalysis: '',

    // Sales Comparison Summary
    salesComparisonSummary: '',
    indicatedValueSCA: '',

    // Appraisal basis
    appraisalBasis: 'as-is', // as-is, subject to completion, subject to repairs, subject to inspection
    appraisalConditions: '',

    // Final opinion
    marketValue: '',
    effectiveDate: new Date().toISOString().split('T')[0],

    // PAGE 3 - COST & INCOME APPROACHES
    // INCOME APPROACH
    estimatedMonthlyRent: '',
    grossRentMultiplier: '',
    incomeApproachSummary: '',
    indicatedValueIncome: '',

    // COST APPROACH
    opinionSiteValue: '',
    dwellingSquareFeet: '',
    dwellingCostPerSF: '',
    dwellingTotalCost: '',
    garageCarportSF: '',
    garageCarportCostPerSF: '',
    garageCarportTotal: '',
    totalEstimatedCostNew: '',
    physicalDepreciation: '',
    functionalDepreciation: '',
    externalDepreciation: '',
    depreciatedCostImprovements: '',
    asIsValueSiteImprovements: '',
    estimatedRemainingLife: '',
    costApproachComments: '',
    costDataSource: '',
    costDataDate: '',
    qualityRating: '',

    // PUD INFORMATION
    pudCommonElementsLeased: false,
    pudRentalTerms: '',
    pudCommonElements: '',
    pudTotalPhases: '',
    pudTotalUnits: '',
    pudUnitsSold: '',
    pudUnitsRented: '',
    pudUnitsForSale: '',
    pudDeveloperControl: false,
    pudUnitType: 'Detached', // Detached, Attached
    pudProjectName: '',
    pudConversion: false,
    pudConversionDate: '',
    pudMultiDwelling: false,
    pudDataSource: '',
    pudComplete: true,
    pudCompletionStatus: '',

    // PAGE 4-6: SCOPE, CERTIFICATIONS, SIGNATURES (Static content mostly)

    // APPRAISER INFORMATION
    appraiserSignature: '',
    appraiserName: '',
    appraiserCompany: '',
    appraiserAddress: '',
    appraiserPhone: '',
    appraiserEmail: '',
    signatureDate: new Date().toISOString().split('T')[0],
    reportDate: new Date().toISOString().split('T')[0],
    inspectionDate: new Date().toISOString().split('T')[0],
    stateCertification: '',
    stateLicense: '',
    stateOther: '',
    appraiserState: '',
    certificationExpiration: '',

    // SUBJECT PROPERTY INSPECTION
    inspectedSubjectProperty: true,
    inspectedExteriorOnly: false,
    inspectedInteriorExterior: true,
    inspectedComparables: true,
    comparableInspectionDate: '',

    // SUPERVISORY APPRAISER (if required)
    supervisoryAppraiser: false,
    supervisorSignature: '',
    supervisorName: '',
    supervisorCompany: '',
    supervisorAddress: '',
    supervisorPhone: '',
    supervisorEmail: '',
    supervisorSignatureDate: '',
    supervisorCertification: '',
    supervisorState: '',
    supervisorExpiration: ''
  });

  const [expandedSections, setExpandedSections] = useState({
    page1: true,
    page2: true,
    page3: true,
    page4: false,
    page5: false,
    page6: false
  });

  // ENHANCED: AI narrative generation state
  const [isGeneratingNarrative, setIsGeneratingNarrative] = useState(false);
  const narrativesGenerated = useRef(false);

  // Auto-populate from subjectProperty
  useEffect(() => {
    if (subjectProperty) {
      console.log('UAD 2.6 Complete Form - Auto-populating:', subjectProperty);

      const yearBuilt = subjectProperty.YearBuilt || '';
      const effectiveAge = yearBuilt ? (new Date().getFullYear() - parseInt(yearBuilt)).toString() : '';

      // Parse utilities
      const utilities = subjectProperty.Utilities || '';
      const electricity = utilities.includes('Electricity') ? 'Public' : 'Other';
      const gas = utilities.includes('Gas') || utilities.includes('Natural Gas') ? 'Public' : 'Other';
      const water = utilities.includes('Water') ? 'Public' : 'Other';
      const sanitarySewer = utilities.includes('Sewer') ? 'Public' : 'Other';

      // Parse appliances
      const appliances = subjectProperty.Appliances || [];
      const refrigerator = appliances.includes('Refrigerator');
      const rangeOven = appliances.includes('Range') || appliances.includes('Oven');
      const dishwasher = appliances.includes('Dishwasher');
      const disposal = appliances.includes('Disposal');
      const microwave = appliances.includes('Microwave');
      const washerDryer = appliances.includes('Washer') && appliances.includes('Dryer');

      setFormData(prev => ({
        ...prev,
        // ENHANCED: Additional PropMix field mappings
        propertyAddress: subjectProperty.StreetAddress || subjectProperty.UnparsedAddress || '',
        city: subjectProperty.City || '',
        state: subjectProperty.StateOrProvince || '',
        zip: subjectProperty.PostalCode || '',
        county: subjectProperty.CountyOrParish || '',
        owner: subjectProperty.OwnerName || '',
        apn: subjectProperty.ParcelNumber || '',
        reTaxes: subjectProperty.TaxAmount || '',
        neighborhoodName: subjectProperty.SubdivisionName || '',
        censusTract: subjectProperty.CensusTract || '',
        area: subjectProperty.LotSizeSquareFeet || '',
        dimensions: subjectProperty.LotSizeDimensions || '',
        zoningClassification: subjectProperty.Zoning || '',
        stories: subjectProperty.StoriesTotal || subjectProperty.Stories || '1',
        yearBuilt: yearBuilt,
        effectiveAge: effectiveAge,
        exteriorWalls: subjectProperty.ConstructionMaterials || '',
        roofSurface: subjectProperty.Roof || '',
        bedrooms: subjectProperty.BedroomsTotal || '',
        bathrooms: subjectProperty.BathroomsTotalDecimal || subjectProperty.BathroomsTotal || '',
        squareFeetGLA: subjectProperty.LivingArea || '',
        contractPrice: subjectProperty.ListPrice || subjectProperty.ClosePrice || '',
        contractDate: subjectProperty.ListingContractDate || subjectProperty.CloseDate || '',
        heatingType: subjectProperty.Heating?.includes('Forced Air') ? 'FWA' : 'FWA',
        coolingType: subjectProperty.Cooling?.includes('Central') ? 'Central Air Conditioning' : '',
        fireplaceNumber: subjectProperty.FireplacesTotal || '0',
        pool: (subjectProperty.PoolFeatures && subjectProperty.PoolFeatures !== 'None') || false,
        garageSpaces: subjectProperty.GarageSpaces || '',

        // ENHANCED: Logic-based mappings
        pud: (subjectProperty.PropertyType?.includes('PUD') || subjectProperty.PropertyType?.includes('Planned Unit Development')) || false,
        hoa: subjectProperty.HOAFee || subjectProperty.AssociationFee || '',
        currentlyOffered: subjectProperty.MlsStatus === 'Active',
        offeringDate: subjectProperty.ListingDate || '',
        offeringPrice: subjectProperty.ListPrice || '',
        units: subjectProperty.PropertyType?.includes('Accessory') ? 'One with Accessory Unit' : 'One',

        // ENHANCED: Additional field mappings
        view: subjectProperty.View || '',
        designStyle: subjectProperty.ArchitecturalStyle || '',
        basementArea: subjectProperty.BasementSqFt || '',
        floors: subjectProperty.Flooring || '',
        totalRooms: subjectProperty.RoomsTotal || '',
        estimatedMonthlyRent: subjectProperty.RentEstimate || '',
        windowType: subjectProperty.WindowFeatures || '',
        garageType: subjectProperty.GarageType || '',
        femaZone: subjectProperty.FloodZone || '',

        // ENHANCED: Parsed utilities
        electricity,
        gas,
        water,
        sanitarySewer,

        // ENHANCED: Parsed appliances
        refrigerator,
        rangeOven,
        dishwasher,
        disposal,
        microwave,
        washerDryer
      }));
    }
  }, [subjectProperty]);

  // ENHANCED: Auto-populate from comparables data
  useEffect(() => {
    if (selectedComparables && selectedComparables.length > 0) {
      console.log('UAD 2.6 - Auto-populating from comparables:', selectedComparables);

      // Calculate price/age ranges from comparables
      const prices = selectedComparables
        .map(c => c.rawData?.ClosePrice || c.rawData?.ListPrice || 0)
        .filter(p => p > 0);
      const ages = selectedComparables
        .map(c => c.rawData?.YearBuilt ? (new Date().getFullYear() - c.rawData.YearBuilt) : 0)
        .filter(a => a > 0);

      if (prices.length > 0) {
        setFormData(prev => ({
          ...prev,
          oneUnitPriceLow: (Math.min(...prices) / 1000).toFixed(0), // in thousands
          oneUnitPriceHigh: (Math.max(...prices) / 1000).toFixed(0),
          oneUnitAgeLow: ages.length > 0 ? Math.min(...ages).toString() : '',
          oneUnitAgeHigh: ages.length > 0 ? Math.max(...ages).toString() : ''
        }));
      }

      // Calculate proximity/distance for each comparable
      if (subjectProperty?.Latitude && subjectProperty?.Longitude) {
        const comparablesWithProximity = selectedComparables.map((comp, index) => {
          const compData = comp.rawData || comp;
          if (compData.Latitude && compData.Longitude) {
            const distance = calculateDistance(
              subjectProperty.Latitude,
              subjectProperty.Longitude,
              compData.Latitude,
              compData.Longitude
            );
            return {
              index,
              distance: distance.toFixed(2),
              address: compData.StreetAddress || compData.UnparsedAddress || '',
              price: compData.ClosePrice || compData.ListPrice || 0,
              saleDate: compData.CloseDate || compData.ListingDate || '',
              gla: compData.LivingArea || 0,
              lotSize: compData.LotSizeSquareFeet || 0,
              yearBuilt: compData.YearBuilt || '',
              bedrooms: compData.BedroomsTotal || '',
              bathrooms: compData.BathroomsTotalDecimal || compData.BathroomsTotal || '',
              garageSpaces: compData.GarageSpaces || '',
              pool: compData.PoolFeatures && compData.PoolFeatures !== 'None',
              condition: compData.PropertyCondition || '',
              view: compData.View || '',
              designStyle: compData.ArchitecturalStyle || ''
            };
          }
          return null;
        }).filter(Boolean);

        console.log('Comparables with proximity:', comparablesWithProximity);
      }
    }
  }, [selectedComparables, subjectProperty]);

  // ENHANCED: Auto-populate from market data (smart adjustments)
  useEffect(() => {
    if (marketData && marketData.featureResults) {
      console.log('UAD 2.6 - Auto-populating from smart adjustments:', marketData);

      // Extract adjustment values from the 8 feature fields
      const adjustmentValues = {
        lotSizePerSqFt: marketData.featureResults.lotSize?.methods?.[0]?.value || 0,
        glaPerSqFt: marketData.featureResults.gla?.methods?.[0]?.value || 0,
        fullBathValue: marketData.featureResults.fullBaths?.methods?.[0]?.value || 0,
        halfBathValue: marketData.featureResults.halfBaths?.methods?.[0]?.value || 0,
        garageSpaceValue: marketData.featureResults.garageSpaces?.methods?.[0]?.value || 0,
        poolValue: marketData.featureResults.poolFeatures?.methods?.[0]?.value || 0,
        exteriorValue: marketData.featureResults.exteriorFeatures?.methods?.[0]?.value || 0,
        conditionPerGrade: marketData.featureResults.conditionQuality?.methods?.[0]?.value || 0
      };

      console.log('Extracted adjustment values:', adjustmentValues);

      // Store for use in comparable adjustments
      setFormData(prev => ({
        ...prev,
        adjustmentValues
      }));
    }
  }, [marketData]);

  // ENHANCED: Auto-generate all AI narratives when data is ready
  useEffect(() => {
    const generateAllNarratives = async () => {
      // Only run once and only if we have data
      if (narrativesGenerated.current) return;

      if (subjectProperty && selectedComparables && selectedComparables.length > 0 && !isGeneratingNarrative) {
        console.log('🤖 Auto-generating all AI narratives on form load...');
        narrativesGenerated.current = true;

        try {
          // Generate neighborhood description
          await generateAINarrative('neighborhoodDescription');
          // Generate market conditions
          await generateAINarrative('marketConditions');
        } catch (error) {
          console.error('Error auto-generating narratives:', error);
        }
      }
    };

    // Small delay to ensure all data is loaded
    const timer = setTimeout(generateAllNarratives, 2000);
    return () => clearTimeout(timer);
  }, [subjectProperty, selectedComparables]);

  // Helper function to calculate distance between two lat/long points (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in miles
  };

  // ENHANCED: Generate AI narrative for various fields
  const generateAINarrative = async (narrativeType) => {
    setIsGeneratingNarrative(true);

    try {
      console.log(`Generating AI narrative for: ${narrativeType}`);

      const response = await apiService.post('/ai/generate-uad-narrative', {
        narrativeType,
        subjectProperty,
        comparableProperties: selectedComparables,
        marketData,
        currentFormData: formData
      });

      if (response.data.success) {
        const generatedText = response.data.narrative;

        // Update the appropriate field based on narrative type
        switch (narrativeType) {
          case 'neighborhoodDescription':
            handleChange('neighborhoodDescription', generatedText);
            break;
          case 'marketConditions':
            handleChange('marketConditions', generatedText);
            break;
          case 'salesComparisonComments':
            handleChange('salesComparisonComments', generatedText);
            break;
          case 'reconciliation':
            handleChange('reconciliation', generatedText);
            break;
          case 'scopeOfWork':
            handleChange('scopeOfWork', generatedText);
            break;
          default:
            console.warn('Unknown narrative type:', narrativeType);
        }

        console.log('✅ AI narrative generated successfully');
      } else {
        throw new Error(response.data.error || 'Failed to generate narrative');
      }
    } catch (error) {
      console.error('❌ Error generating AI narrative:', error);
    } finally {
      setIsGeneratingNarrative(false);
    }
  };

  // Update parent component using ref to avoid re-renders
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (onUpdateRef.current) {
      const timer = setTimeout(() => {
        onUpdateRef.current(formData);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData]);

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Reusable field component
  // Wrapper functions to pass formData and handleChange
  const FieldWrapper = (props) => (
    <Field
      {...props}
      value={formData[props.field]}
      onChange={(e) => {
        const val = props.type === 'checkbox' ? e.target.checked : e.target.value;
        handleChange(props.field, val);
      }}
    />
  );

  const CheckboxFieldWrapper = (props) => (
    <CheckboxField
      {...props}
      checked={formData[props.field]}
      onChange={(e) => handleChange(props.field, e.target.checked)}
    />
  );

  const SectionHeader = ({ title, section, bgColor = 'bg-gray-800' }) => (
    <button
      onClick={() => toggleSection(section)}
      className={`w-full ${bgColor} text-white px-4 py-3 font-bold text-left flex items-center justify-between hover:opacity-90 transition-opacity`}
    >
      <span>{title}</span>
      {expandedSections[section] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
    </button>
  );

  return (
    <div className="bg-white rounded-lg shadow-2xl border-4 border-gray-900">
      {/* FORM HEADER */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 text-center border-b-4 border-gray-900">
        <h1 className="text-3xl font-bold">Uniform Residential Appraisal Report</h1>
        <p className="text-sm mt-2">Fannie Mae Form 1004 / Freddie Mac Form 70 • March 2005 • UAD 2.6</p>
        <p className="text-xs mt-1 text-blue-200">
          The purpose of this summary appraisal report is to provide the lender/client with an accurate, and adequately supported, opinion of the market value of the subject property.
        </p>
      </div>

      {/* ==================== PAGE 1 ==================== */}
      <div className="border-b-4 border-gray-900">
        <SectionHeader title="PAGE 1: SUBJECT PROPERTY & IMPROVEMENTS" section="page1" />
        {expandedSections.page1 && (
          <div className="p-6 space-y-6">

            {/* SUBJECT PROPERTY IDENTIFICATION */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">SUBJECT PROPERTY</h3>
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2">
                  <FieldWrapper label="Property Address" field="propertyAddress" />
                </div>
                <FieldWrapper label="City" field="city" />
                <div className="grid grid-cols-2 gap-2">
                  <FieldWrapper label="State" field="state" />
                  <FieldWrapper label="Zip Code" field="zip" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <FieldWrapper label="Borrower" field="borrower" />
                <FieldWrapper label="Owner of Public Record" field="owner" />
              </div>
              <div className="grid grid-cols-1 gap-3 mt-3">
                <FieldWrapper label="Legal Description" field="legalDescription" type="textarea" />
              </div>
              <div className="grid grid-cols-4 gap-3 mt-3">
                <FieldWrapper label="Assessor's Parcel #" field="apn" />
                <FieldWrapper label="Tax Year" field="taxYear" type="number" />
                <FieldWrapper label="R.E. Taxes $" field="reTaxes" />
                <FieldWrapper label="County" field="county" />
              </div>
              <div className="grid grid-cols-4 gap-3 mt-3">
                <FieldWrapper label="Neighborhood Name" field="neighborhoodName" />
                <FieldWrapper label="Map Reference" field="mapReference" />
                <FieldWrapper label="Census Tract" field="censusTract" />
                <FieldWrapper label="Occupant" field="occupant" options={['Owner', 'Tenant', 'Vacant']} />
              </div>
              <div className="grid grid-cols-4 gap-3 mt-3">
                <FieldWrapper label="Special Assessments $" field="specialAssessments" />
                <div className="flex items-center space-x-4">
                  <CheckboxFieldWrapper label="PUD" field="pud" />
                </div>
                <FieldWrapper label="HOA $" field="hoa" />
                <FieldWrapper label="per" field="hoaPer" options={['year', 'month']} />
              </div>
            </div>

            {/* PROPERTY RIGHTS & ASSIGNMENT */}
            <div className="border-2 border-gray-800 p-4">
              <div className="grid grid-cols-3 gap-3">
                <FieldWrapper label="Property Rights Appraised" field="propertyRights" options={['Fee Simple', 'Leasehold', 'Other']} />
                <FieldWrapper label="Assignment Type" field="assignmentType" options={['Purchase Transaction', 'Refinance Transaction', 'Other']} />
                <FieldWrapper label="Lender/Client" field="lenderClient" />
              </div>
              <div className="mt-3">
                <FieldWrapper label="Lender/Client Address" field="lenderAddress" />
              </div>
            </div>

            {/* CURRENT OFFERING & CONTRACT */}
            <div className="border-2 border-gray-800 p-4">
              <div className="flex items-center space-x-4 mb-3">
                <span className="text-sm font-semibold text-gray-900">Is the subject property currently offered for sale or has it been offered for sale in the twelve months prior to the effective date of this appraisal?</span>
                <label className="flex items-center text-gray-900"><input type="radio" checked={formData.currentlyOffered === true} onChange={() => handleChange('currentlyOffered', true)} className="mr-1" /> Yes</label>
                <label className="flex items-center text-gray-900"><input type="radio" checked={formData.currentlyOffered === false} onChange={() => handleChange('currentlyOffered', false)} className="mr-1" /> No</label>
              </div>
              {formData.currentlyOffered && (
                <div className="grid grid-cols-3 gap-3">
                  <FieldWrapper label="Data Source(s)" field="offeringDataSources" />
                  <FieldWrapper label="Offering Price(s)" field="offeringPrice" />
                  <FieldWrapper label="Date(s)" field="offeringDate" type="date" />
                </div>
              )}
            </div>

            {/* CONTRACT ANALYSIS */}
            <div className="border-2 border-gray-800 p-4">
              <div className="flex items-center space-x-4 mb-3">
                <span className="text-xs font-semibold text-gray-900">I</span>
                <label className="flex items-center text-gray-900"><input type="radio" checked={formData.analyzeContract === true} onChange={() => handleChange('analyzeContract', true)} className="mr-1" /> did</label>
                <label className="flex items-center text-gray-900"><input type="radio" checked={formData.analyzeContract === false} onChange={() => handleChange('analyzeContract', false)} className="mr-1" /> did not</label>
                <span className="text-sm text-gray-900">analyze the contract for sale for the subject purchase transaction.</span>
              </div>
              <FieldWrapper label="Explain the results of the analysis of the contract for sale or why the analysis was not performed." field="contractAnalysisResults" type="textarea" />
              <div className="grid grid-cols-4 gap-3 mt-3">
                <FieldWrapper label="Contract Price $" field="contractPrice" />
                <FieldWrapper label="Date of Contract" field="contractDate" type="date" />
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-900">Is the property seller the owner of public record?</span>
                  <label className="text-gray-900"><input type="radio" checked={formData.sellerOwner === true} onChange={() => handleChange('sellerOwner', true)} className="mr-1" /> Yes</label>
                  <label className="text-gray-900"><input type="radio" checked={formData.sellerOwner === false} onChange={() => handleChange('sellerOwner', false)} className="mr-1" /> No</label>
                </div>
                <FieldWrapper label="Data Source(s)" field="dataSource" />
              </div>
              <div className="mt-3">
                <div className="flex items-center space-x-4 mb-2">
                  <span className="text-sm text-gray-900">Is there any financial assistance (loan charges, sale concessions, gift or downpayment assistance, etc.) to be paid by any party on behalf of the borrower?</span>
                  <label className="text-gray-900"><input type="radio" checked={formData.financialAssistance === true} onChange={() => handleChange('financialAssistance', true)} className="mr-1" /> Yes</label>
                  <label className="text-gray-900"><input type="radio" checked={formData.financialAssistance === false} onChange={() => handleChange('financialAssistance', false)} className="mr-1" /> No</label>
                </div>
                {formData.financialAssistance && (
                  <div className="grid grid-cols-2 gap-3">
                    <FieldWrapper label="Total Dollar Amount" field="financialAssistanceAmount" />
                    <FieldWrapper label="Describe Items to be Paid" field="financialAssistanceDescription" />
                  </div>
                )}
              </div>
            </div>

            {/* NEIGHBORHOOD DESCRIPTION */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">NEIGHBORHOOD DESCRIPTION</h3>
              <p className="text-xs italic mb-3 text-gray-900">Note: Race and the racial composition of the neighborhood are not appraisal factors.</p>

              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Location</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.location === 'Urban'} onChange={() => handleChange('location', 'Urban')} className="mr-1" /> Urban</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.location === 'Suburban'} onChange={() => handleChange('location', 'Suburban')} className="mr-1" /> Suburban</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.location === 'Rural'} onChange={() => handleChange('location', 'Rural')} className="mr-1" /> Rural</label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Built-Up</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.builtUp === 'Over 75%'} onChange={() => handleChange('builtUp', 'Over 75%')} className="mr-1" /> Over 75%</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.builtUp === '25-75%'} onChange={() => handleChange('builtUp', '25-75%')} className="mr-1" /> 25-75%</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.builtUp === 'Under 25%'} onChange={() => handleChange('builtUp', 'Under 25%')} className="mr-1" /> Under 25%</label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Growth</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.growth === 'Rapid'} onChange={() => handleChange('growth', 'Rapid')} className="mr-1" /> Rapid</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.growth === 'Stable'} onChange={() => handleChange('growth', 'Stable')} className="mr-1" /> Stable</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.growth === 'Slow'} onChange={() => handleChange('growth', 'Slow')} className="mr-1" /> Slow</label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Property Values</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.propertyValues === 'Increasing'} onChange={() => handleChange('propertyValues', 'Increasing')} className="mr-1" /> Increasing</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.propertyValues === 'Stable'} onChange={() => handleChange('propertyValues', 'Stable')} className="mr-1" /> Stable</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.propertyValues === 'Declining'} onChange={() => handleChange('propertyValues', 'Declining')} className="mr-1" /> Declining</label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Demand/Supply</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.demandSupply === 'Shortage'} onChange={() => handleChange('demandSupply', 'Shortage')} className="mr-1" /> Shortage</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.demandSupply === 'In Balance'} onChange={() => handleChange('demandSupply', 'In Balance')} className="mr-1" /> In Balance</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.demandSupply === 'Over Supply'} onChange={() => handleChange('demandSupply', 'Over Supply')} className="mr-1" /> Over Supply</label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Marketing Time</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.marketingTime === 'Under 3 mths'} onChange={() => handleChange('marketingTime', 'Under 3 mths')} className="mr-1" /> Under 3 mths</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.marketingTime === '3-6 mths'} onChange={() => handleChange('marketingTime', '3-6 mths')} className="mr-1" /> 3-6 mths</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.marketingTime === 'Over 6 mths'} onChange={() => handleChange('marketingTime', 'Over 6 mths')} className="mr-1" /> Over 6 mths</label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">One-Unit Housing PRICE ($000) / AGE (yrs)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <FieldWrapper label="Low" field="oneUnitPriceLow" />
                    <FieldWrapper label="Low" field="oneUnitAgeLow" />
                    <FieldWrapper label="High" field="oneUnitPriceHigh" />
                    <FieldWrapper label="High" field="oneUnitAgeHigh" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-5 gap-3 mt-3">
                <FieldWrapper label="One-Unit %" field="oneUnitPercent" placeholder="%" />
                <FieldWrapper label="2-4 Unit %" field="twoFourUnitPercent" placeholder="%" />
                <FieldWrapper label="Multi-Family %" field="multiFamilyPercent" placeholder="%" />
                <FieldWrapper label="Commercial %" field="commercialPercent" placeholder="%" />
                <FieldWrapper label="Other %" field="otherPercent" placeholder="%" />
              </div>

              <div className="mt-3">
                <FieldWrapper label="Neighborhood Boundaries" field="neighborhoodBoundaries" type="textarea" placeholder="Describe the boundaries of the neighborhood" />
              </div>

              <div className="mt-3">
                <div className="relative">
                  <FieldWrapper label="Neighborhood Description" field="neighborhoodDescription" type="textarea" placeholder="Describe the neighborhood" />
                  <button
                    type="button"
                    onClick={() => generateAINarrative('neighborhoodDescription')}
                    disabled={isGeneratingNarrative}
                    className="absolute top-0 right-0 mt-6 mr-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {isGeneratingNarrative ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                    Generate AI
                  </button>
                </div>
              </div>

              <div className="mt-3">
                <div className="relative">
                  <FieldWrapper label="Market Conditions (including support for the above conclusions)" field="marketConditions" type="textarea" />
                  <button
                    type="button"
                    onClick={() => generateAINarrative('marketConditions')}
                    disabled={isGeneratingNarrative}
                    className="absolute top-0 right-0 mt-6 mr-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {isGeneratingNarrative ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                    Generate AI
                  </button>
                </div>
              </div>
            </div>

            {/* SITE */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">SITE</h3>
              <div className="grid grid-cols-4 gap-3">
                <FieldWrapper label="Dimensions" field="dimensions" />
                <FieldWrapper label="Area (sq ft)" field="area" />
                <FieldWrapper label="Shape" field="shape" />
                <FieldWrapper label="View" field="view" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <FieldWrapper label="Specific Zoning Classification" field="zoningClassification" />
                <FieldWrapper label="Zoning Description" field="zoningDescription" />
              </div>
              <div className="mt-3">
                <label className="block text-xs font-semibold mb-1 text-gray-900">Zoning Compliance</label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.zoningCompliance === 'Legal'} onChange={() => handleChange('zoningCompliance', 'Legal')} className="mr-1" /> Legal</label>
                  <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.zoningCompliance === 'Legal Nonconforming'} onChange={() => handleChange('zoningCompliance', 'Legal Nonconforming')} className="mr-1" /> Legal Nonconforming (Grandfathered Use)</label>
                  <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.zoningCompliance === 'No Zoning'} onChange={() => handleChange('zoningCompliance', 'No Zoning')} className="mr-1" /> No Zoning</label>
                  <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.zoningCompliance === 'Illegal'} onChange={() => handleChange('zoningCompliance', 'Illegal')} className="mr-1" /> Illegal</label>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex items-center space-x-4 mb-2">
                  <span className="text-sm text-gray-900">Is the highest and best use of the subject property as improved (or as proposed per plans and specifications) the present use?</span>
                  <label className="text-gray-900"><input type="radio" checked={formData.highestBestUse === true} onChange={() => handleChange('highestBestUse', true)} className="mr-1" /> Yes</label>
                  <label className="text-gray-900"><input type="radio" checked={formData.highestBestUse === false} onChange={() => handleChange('highestBestUse', false)} className="mr-1" /> No</label>
                </div>
                {!formData.highestBestUse && (
                  <FieldWrapper label="If No, describe" field="highestBestUseDescription" type="textarea" />
                )}
              </div>
              <div className="mt-3">
                <div className="flex items-center space-x-4 mb-2">
                  <span className="text-sm text-gray-900">Are there any adverse site conditions or external factors (easements, encroachments, environmental conditions, land uses, etc.)?</span>
                  <label className="text-gray-900"><input type="radio" checked={formData.adverseSiteConditions === true} onChange={() => handleChange('adverseSiteConditions', true)} className="mr-1" /> Yes</label>
                  <label className="text-gray-900"><input type="radio" checked={formData.adverseSiteConditions === false} onChange={() => handleChange('adverseSiteConditions', false)} className="mr-1" /> No</label>
                </div>
                {formData.adverseSiteConditions && (
                  <FieldWrapper label="If Yes, describe" field="adverseSiteDescription" type="textarea" />
                )}
              </div>
            </div>

            {/* UTILITIES & IMPROVEMENTS */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">UTILITIES & OFF-SITE IMPROVEMENTS</h3>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Electricity</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.electricity === 'Public'} onChange={() => handleChange('electricity', 'Public')} className="mr-1" /> Public</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.electricity === 'Other'} onChange={() => handleChange('electricity', 'Other')} className="mr-1" /> Other</label>
                  </div>
                  {formData.electricity === 'Other' && <FieldWrapper label="" field="electricityOther" placeholder="Describe" />}
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Gas</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.gas === 'Public'} onChange={() => handleChange('gas', 'Public')} className="mr-1" /> Public</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.gas === 'Other'} onChange={() => handleChange('gas', 'Other')} className="mr-1" /> Other</label>
                  </div>
                  {formData.gas === 'Other' && <FieldWrapper label="" field="gasOther" placeholder="Describe" />}
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Water</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.water === 'Public'} onChange={() => handleChange('water', 'Public')} className="mr-1" /> Public</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.water === 'Other'} onChange={() => handleChange('water', 'Other')} className="mr-1" /> Other</label>
                  </div>
                  {formData.water === 'Other' && <FieldWrapper label="" field="waterOther" placeholder="Describe" />}
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Sanitary Sewer</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.sanitarySewer === 'Public'} onChange={() => handleChange('sanitarySewer', 'Public')} className="mr-1" /> Public</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.sanitarySewer === 'Other'} onChange={() => handleChange('sanitarySewer', 'Other')} className="mr-1" /> Other</label>
                  </div>
                  {formData.sanitarySewer === 'Other' && <FieldWrapper label="" field="sanitarySewerOther" placeholder="Describe" />}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-3">
                <FieldWrapper label="Street Type" field="streetType" options={['Public', 'Private']} />
                <FieldWrapper label="Alley" field="alley" options={['None', 'Public', 'Private']} />
              </div>

              <div className="mt-3">
                <div className="flex items-center space-x-4 mb-2">
                  <span className="text-sm font-semibold text-gray-900">FEMA Special Flood Hazard Area</span>
                  <label className="text-gray-900"><input type="radio" checked={formData.femaFloodHazard === true} onChange={() => handleChange('femaFloodHazard', true)} className="mr-1" /> Yes</label>
                  <label className="text-gray-900"><input type="radio" checked={formData.femaFloodHazard === false} onChange={() => handleChange('femaFloodHazard', false)} className="mr-1" /> No</label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FieldWrapper label="FEMA Flood Zone" field="femaZone" />
                  <FieldWrapper label="FEMA Map #" field="femaMap" />
                  <FieldWrapper label="FEMA Map Date" field="femaMapDate" type="date" />
                </div>
              </div>

              <div className="mt-3">
                <div className="flex items-center space-x-4 mb-2">
                  <span className="text-sm text-gray-900">Are the utilities and off-site improvements typical for the market area?</span>
                  <label className="text-gray-900"><input type="radio" checked={formData.utilitiesTypical === true} onChange={() => handleChange('utilitiesTypical', true)} className="mr-1" /> Yes</label>
                  <label className="text-gray-900"><input type="radio" checked={formData.utilitiesTypical === false} onChange={() => handleChange('utilitiesTypical', false)} className="mr-1" /> No</label>
                </div>
                {!formData.utilitiesTypical && (
                  <FieldWrapper label="If No, describe" field="utilitiesDescription" type="textarea" />
                )}
              </div>
            </div>

            {/* IMPROVEMENTS - GENERAL DESCRIPTION */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">IMPROVEMENTS - GENERAL DESCRIPTION</h3>
              <div className="grid grid-cols-4 gap-3">
                <FieldWrapper label="Units" field="units" options={['One', 'One with Accessory Unit']} />
                <FieldWrapper label="# of Stories" field="stories" />
                <FieldWrapper label="Type" field="type" options={['Det.', 'Att.', 'S-Det./End Unit']} />
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Status</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.existing === true} onChange={() => handleChange('existing', true)} className="mr-1" /> Existing</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.existing === false} onChange={() => handleChange('existing', false)} className="mr-1" /> Proposed / Under Const.</label>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <FieldWrapper label="Design (Style)" field="designStyle" />
                <FieldWrapper label="Year Built" field="yearBuilt" type="number" />
                <FieldWrapper label="Effective Age (Yrs)" field="effectiveAge" type="number" />
              </div>
            </div>

            {/* FOUNDATION */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">FOUNDATION</h3>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Type</label>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.foundation === 'Concrete Slab'} onChange={() => handleChange('foundation', 'Concrete Slab')} className="mr-1" /> Concrete Slab</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.foundation === 'Crawl Space'} onChange={() => handleChange('foundation', 'Crawl Space')} className="mr-1" /> Crawl Space</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.foundation === 'Full Basement'} onChange={() => handleChange('foundation', 'Full Basement')} className="mr-1" /> Full Basement</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.foundation === 'Partial Basement'} onChange={() => handleChange('foundation', 'Partial Basement')} className="mr-1" /> Partial Basement</label>
                  </div>
                </div>
                {(formData.foundation === 'Full Basement' || formData.foundation === 'Partial Basement') && (
                  <>
                    <FieldWrapper label="Basement Area (sq. ft.)" field="basementArea" type="number" />
                    <FieldWrapper label="Basement Finish %" field="basementFinishPercent" type="number" placeholder="%" />
                    <div>
                      <CheckboxFieldWrapper label="Outside Entry/Exit" field="outsideEntry" />
                      <CheckboxFieldWrapper label="Sump Pump" field="sumpPump" />
                    </div>
                  </>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <CheckboxFieldWrapper label="Evidence of Infestation" field="evidenceInfestation" />
                <CheckboxFieldWrapper label="Dampness" field="dampness" />
                <CheckboxFieldWrapper label="Settlement" field="settlement" />
              </div>
            </div>

            {/* EXTERIOR DESCRIPTION */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">EXTERIOR DESCRIPTION</h3>
              <div className="grid grid-cols-2 gap-3">
                <FieldWrapper label="Foundation Walls" field="foundationWalls" />
                <FieldWrapper label="Exterior Walls" field="exteriorWalls" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <FieldWrapper label="Roof Surface" field="roofSurface" />
                <FieldWrapper label="Gutters & Downspouts" field="guttersDownspouts" />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <FieldWrapper label="Window Type" field="windowType" />
                <CheckboxFieldWrapper label="Storm Sash/Insulated" field="stormSash" />
                <CheckboxFieldWrapper label="Screens" field="screens" />
              </div>
            </div>

            {/* INTERIOR */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">INTERIOR materials/condition</h3>
              <div className="grid grid-cols-3 gap-3">
                <FieldWrapper label="Floors" field="floors" />
                <FieldWrapper label="Walls" field="walls" />
                <FieldWrapper label="Trim/Finish" field="trimFinish" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <FieldWrapper label="Bath Floor" field="bathFloor" />
                <FieldWrapper label="Bath Wainscot" field="bathWainscot" />
              </div>
            </div>

            {/* CAR STORAGE */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">CAR STORAGE</h3>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <CheckboxFieldWrapper label="None" field="carStorage" />
                </div>
                <div>
                  <CheckboxFieldWrapper label="Driveway" field="driveway" />
                  {formData.driveway && (
                    <>
                      <FieldWrapper label="# of Cars" field="drivewaySpaces" type="number" />
                      <FieldWrapper label="Surface" field="drivewaySurface" />
                    </>
                  )}
                </div>
                <div>
                  <CheckboxFieldWrapper label="Garage" field="garage" />
                  {formData.garage && (
                    <>
                      <FieldWrapper label="# of Cars" field="garageSpaces" type="number" />
                      <FieldWrapper label="Type" field="garageType" options={['Att.', 'Det.', 'Built-in']} />
                    </>
                  )}
                </div>
                <div>
                  <CheckboxFieldWrapper label="Carport" field="carport" />
                  {formData.carport && (
                    <>
                      <FieldWrapper label="# of Cars" field="carportSpaces" type="number" />
                      <FieldWrapper label="Type" field="carportType" options={['Att.', 'Det.']} />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ATTIC, HEATING, COOLING, AMENITIES */}
            <div className="border-2 border-gray-800 p-4">
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <h4 className="font-semibold text-xs mb-2 text-gray-900">ATTIC</h4>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.atticType === 'None'} onChange={() => handleChange('atticType', 'None')} className="mr-1" /> None</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.atticType === 'Drop Stair'} onChange={() => handleChange('atticType', 'Drop Stair')} className="mr-1" /> Drop Stair</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.atticType === 'Stairs'} onChange={() => handleChange('atticType', 'Stairs')} className="mr-1" /> Stairs</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.atticType === 'Floor'} onChange={() => handleChange('atticType', 'Floor')} className="mr-1" /> Floor</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.atticType === 'Scuttle'} onChange={() => handleChange('atticType', 'Scuttle')} className="mr-1" /> Scuttle</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.atticType === 'Finished'} onChange={() => handleChange('atticType', 'Finished')} className="mr-1" /> Finished</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.atticType === 'Heated'} onChange={() => handleChange('atticType', 'Heated')} className="mr-1" /> Heated</label>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-xs mb-2 text-gray-900">HEATING</h4>
                  <FieldWrapper label="Type" field="heatingType" options={['FWA', 'HWBB', 'Radiant', 'Other']} />
                  <FieldWrapper label="Fuel" field="heatingFuel" options={['Gas', 'Electric', 'Oil', 'Solar', 'Other']} />
                </div>
                <div>
                  <h4 className="font-semibold text-xs mb-2 text-gray-900">COOLING</h4>
                  <div className="space-y-1">
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.coolingType === 'Central Air Conditioning'} onChange={() => handleChange('coolingType', 'Central Air Conditioning')} className="mr-1" /> Central Air Conditioning</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.coolingType === 'Individual'} onChange={() => handleChange('coolingType', 'Individual')} className="mr-1" /> Individual</label>
                    <label className="flex items-center text-xs text-gray-900"><input type="radio" checked={formData.coolingType === 'Other'} onChange={() => handleChange('coolingType', 'Other')} className="mr-1" /> Other</label>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <h4 className="font-semibold text-xs mb-2 text-gray-900">AMENITIES</h4>
                <div className="grid grid-cols-6 gap-3">
                  <div>
                    <CheckboxFieldWrapper label="Fireplace(s)" field="fireplace" />
                    {formData.fireplace && <FieldWrapper label="#" field="fireplaceNumber" type="number" />}
                  </div>
                  <div>
                    <CheckboxFieldWrapper label="Woodstove(s)" field="woodstove" />
                    {formData.woodstove && <FieldWrapper label="#" field="woodstoveNumber" type="number" />}
                  </div>
                  <CheckboxFieldWrapper label="Fence" field="fence" />
                  <CheckboxFieldWrapper label="Patio/Deck" field="patioDeck" />
                  <CheckboxFieldWrapper label="Pool" field="pool" />
                  <CheckboxFieldWrapper label="Porch" field="porch" />
                </div>
                <div className="mt-2">
                  <FieldWrapper label="Other" field="other" placeholder="Other amenities" />
                </div>
              </div>
            </div>

            {/* APPLIANCES */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">APPLIANCES</h3>
              <div className="grid grid-cols-6 gap-3">
                <CheckboxFieldWrapper label="Refrigerator" field="refrigerator" />
                <CheckboxFieldWrapper label="Range/Oven" field="rangeOven" />
                <CheckboxFieldWrapper label="Dishwasher" field="dishwasher" />
                <CheckboxFieldWrapper label="Disposal" field="disposal" />
                <CheckboxFieldWrapper label="Microwave" field="microwave" />
                <CheckboxFieldWrapper label="Washer/Dryer" field="washerDryer" />
              </div>
              <div className="mt-2">
                <FieldWrapper label="Other (describe)" field="appliancesOther" />
              </div>
            </div>

            {/* FINISHED AREA & ROOMS */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">FINISHED AREA ABOVE GRADE</h3>
              <div className="grid grid-cols-4 gap-3">
                <FieldWrapper label="Total Rooms" field="totalRooms" type="number" />
                <FieldWrapper label="Bedrooms" field="bedrooms" type="number" />
                <FieldWrapper label="Bath(s)" field="bathrooms" />
                <FieldWrapper label="Square Feet of Gross Living Area Above Grade" field="squareFeetGLA" type="number" />
              </div>
            </div>

            {/* ADDITIONAL FEATURES */}
            <div className="border-2 border-gray-800 p-4">
              <FieldWrapper label="Additional features (special energy efficient items, etc.)" field="additionalFeatures" type="textarea" />
            </div>

            {/* CONDITION DESCRIPTION */}
            <div className="border-2 border-gray-800 p-4">
              <FieldWrapper label="Describe the condition of the property (including needed repairs, deterioration, renovations, remodeling, etc.)." field="conditionDescription" type="textarea" />
            </div>

            {/* PHYSICAL DEFICIENCIES */}
            <div className="border-2 border-gray-800 p-4">
              <div className="flex items-center space-x-4 mb-2">
                <span className="text-sm text-gray-900">Are there any physical deficiencies or adverse conditions that affect the livability, soundness, or structural integrity of the property?</span>
                <label className="text-gray-900"><input type="radio" checked={formData.physicalDeficiencies === true} onChange={() => handleChange('physicalDeficiencies', true)} className="mr-1" /> Yes</label>
                <label className="text-gray-900"><input type="radio" checked={formData.physicalDeficiencies === false} onChange={() => handleChange('physicalDeficiencies', false)} className="mr-1" /> No</label>
              </div>
              {formData.physicalDeficiencies && (
                <FieldWrapper label="If Yes, describe" field="physicalDeficienciesDescription" type="textarea" />
              )}
            </div>

            {/* CONFORMITY */}
            <div className="border-2 border-gray-800 p-4">
              <div className="flex items-center space-x-4 mb-2">
                <span className="text-sm text-gray-900">Does the property generally conform to the neighborhood (functional utility, style, condition, use, construction, etc.)?</span>
                <label className="text-gray-900"><input type="radio" checked={formData.conformsToNeighborhood === true} onChange={() => handleChange('conformsToNeighborhood', true)} className="mr-1" /> Yes</label>
                <label className="text-gray-900"><input type="radio" checked={formData.conformsToNeighborhood === false} onChange={() => handleChange('conformsToNeighborhood', false)} className="mr-1" /> No</label>
              </div>
              {!formData.conformsToNeighborhood && (
                <FieldWrapper label="If No, describe" field="conformityDescription" type="textarea" />
              )}
            </div>

          </div>
        )}
      </div>

      {/* ==================== PAGE 2 - SALES COMPARISON APPROACH ==================== */}
      <div className="border-b-4 border-gray-900">
        <SectionHeader title="PAGE 2: SALES COMPARISON APPROACH" section="page2" bgColor="bg-blue-800" />
        {expandedSections.page2 && (
          <div className="p-6 space-y-6">
            {/* COMPARABLE SALES/LISTINGS RANGES */}
            <div className="border-2 border-gray-800 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">There are comparable properties currently offered for sale in the subject neighborhood ranging in price from:</label>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-900">$</span>
                    <FieldWrapper label="" field="comparableListingsRangeLow" placeholder="Low" />
                    <span className="text-xs text-gray-900">to $</span>
                    <FieldWrapper label="" field="comparableListingsRangeHigh" placeholder="High" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">There are comparable sales in the subject neighborhood within the past twelve months ranging in sale price from:</label>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-900">$</span>
                    <FieldWrapper label="" field="comparableSalesRangeLow" placeholder="Low" />
                    <span className="text-xs text-gray-900">to $</span>
                    <FieldWrapper label="" field="comparableSalesRangeHigh" placeholder="High" />
                  </div>
                </div>
              </div>
            </div>

            {/* SALES COMPARISON GRID - DYNAMIC */}
            <ComparablesSalesGrid
              subjectProperty={subjectProperty}
              formData={formData}
              selectedComparables={selectedComparables}
              handleChange={handleChange}
            />

            {/* PRIOR SALES RESEARCH */}
            <div className="border-2 border-gray-800 p-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <span className="text-xs text-gray-900">I</span>
                  <label className="text-gray-900"><input type="radio" checked={formData.researchedPriorSales === true} onChange={() => handleChange('researchedPriorSales', true)} className="mr-1" /> did</label>
                  <label className="text-gray-900"><input type="radio" checked={formData.researchedPriorSales === false} onChange={() => handleChange('researchedPriorSales', false)} className="mr-1" /> did not</label>
                  <span className="text-sm text-gray-900">research the sale or transfer history of the subject property and comparable sales. If not, explain</span>
                </div>

                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-900">My research</span>
                  <label className="text-gray-900"><input type="radio" checked={formData.subjectPriorSalesThreeYears === true} onChange={() => handleChange('subjectPriorSalesThreeYears', true)} className="mr-1" /> did</label>
                  <label className="text-gray-900"><input type="radio" checked={formData.subjectPriorSalesThreeYears === false} onChange={() => handleChange('subjectPriorSalesThreeYears', false)} className="mr-1" /> did not</label>
                  <span className="text-sm text-gray-900">reveal any prior sales or transfers of the subject property for the three years prior to the effective date of this appraisal.</span>
                </div>
                <FieldWrapper label="Data source(s)" field="subjectPriorSalesDataSource" />

                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-900">My research</span>
                  <label className="text-gray-900"><input type="radio" checked={formData.comparablePriorSalesOneYear === true} onChange={() => handleChange('comparablePriorSalesOneYear', true)} className="mr-1" /> did</label>
                  <label className="text-gray-900"><input type="radio" checked={formData.comparablePriorSalesOneYear === false} onChange={() => handleChange('comparablePriorSalesOneYear', false)} className="mr-1" /> did not</label>
                  <span className="text-sm text-gray-900">reveal any prior sales or transfers of the comparable sales for the year prior to the date of sale of the comparable sale.</span>
                </div>
                <FieldWrapper label="Data source(s)" field="comparablePriorSalesDataSource" />

                <FieldWrapper label="Analysis of prior sale or transfer history of the subject property and comparable sales" field="priorSaleAnalysis" type="textarea" />
              </div>
            </div>

            {/* SALES COMPARISON SUMMARY */}
            <div className="border-2 border-gray-800 p-4">
              <FieldWrapper label="Summary of Sales Comparison Approach" field="salesComparisonSummary" type="textarea" placeholder="Provide a detailed summary of your sales comparison analysis" />
            </div>

            {/* INDICATED VALUES */}
            <div className="border-2 border-gray-800 p-4 bg-yellow-50">
              <h3 className="font-bold text-sm mb-3 text-gray-900">INDICATED VALUE BY APPROACHES</h3>
              <div className="grid grid-cols-3 gap-3">
                <FieldWrapper label="Sales Comparison Approach $" field="indicatedValueSCA" />
                <FieldWrapper label="Cost Approach (if developed) $" field="indicatedValueCost" />
                <FieldWrapper label="Income Approach (if developed) $" field="indicatedValueIncome" />
              </div>
            </div>

            {/* APPRAISAL BASIS */}
            <div className="border-2 border-gray-800 p-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-900">This appraisal is made:</label>
                  <div className="space-y-2">
                    <label className="flex items-center text-xs text-gray-900">
                      <input type="radio" checked={formData.appraisalBasis === 'as-is'} onChange={() => handleChange('appraisalBasis', 'as-is')} className="mr-2" />
                      "as is"
                    </label>
                    <label className="flex items-center text-xs text-gray-900">
                      <input type="radio" checked={formData.appraisalBasis === 'subject to completion'} onChange={() => handleChange('appraisalBasis', 'subject to completion')} className="mr-2" />
                      subject to completion per plans and specifications on the basis of a hypothetical condition that the improvements have been completed
                    </label>
                    <label className="flex items-center text-xs text-gray-900">
                      <input type="radio" checked={formData.appraisalBasis === 'subject to repairs'} onChange={() => handleChange('appraisalBasis', 'subject to repairs')} className="mr-2" />
                      subject to the following repairs or alterations on the basis of a hypothetical condition that the repairs or alterations have been completed
                    </label>
                    <label className="flex items-center text-xs text-gray-900">
                      <input type="radio" checked={formData.appraisalBasis === 'subject to inspection'} onChange={() => handleChange('appraisalBasis', 'subject to inspection')} className="mr-2" />
                      subject to the following required inspection based on the extraordinary assumption that the condition or deficiency does not require alteration or repair
                    </label>
                  </div>
                </div>
                {formData.appraisalBasis !== 'as-is' && (
                  <FieldWrapper label="Describe conditions/repairs" field="appraisalConditions" type="textarea" />
                )}
              </div>
            </div>

            {/* FINAL OPINION OF VALUE */}
            <div className="border-4 border-green-600 bg-green-50 p-6">
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-green-900">FINAL OPINION OF MARKET VALUE</h2>
              </div>
              <div className="space-y-3">
                <div className="text-sm text-gray-900">
                  Based on a complete visual inspection of the interior and exterior areas of the subject property, defined scope of work, statement of assumptions and limiting conditions, and appraiser's certification, my (our) opinion of the market value, as defined, of the real property that is the subject of this report is
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FieldWrapper label="Market Value $" field="marketValue" />
                  <FieldWrapper label="as of (Effective Date)" field="effectiveDate" type="date" />
                </div>
                <div className="text-xs italic text-gray-600">
                  which is the date of inspection and the effective date of this appraisal.
                </div>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* ==================== PAGE 3 - COST & INCOME APPROACHES ==================== */}
      <div className="border-b-4 border-gray-900">
        <SectionHeader title="PAGE 3: COST & INCOME APPROACHES, PUD INFORMATION" section="page3" bgColor="bg-green-800" />
        {expandedSections.page3 && (
          <div className="p-6 space-y-6">

            {/* COST APPROACH */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">COST APPROACH TO VALUE (not required by Fannie Mae)</h3>
              <p className="text-xs mb-3 text-gray-900">Provide adequate information for the lender/client to replicate the below cost figures and calculations.</p>

              <FieldWrapper label="Support for the opinion of site value (summary of comparable land sales or other methods for estimating site value)" field="opinionSiteValue" type="textarea" />

              <div className="grid grid-cols-3 gap-3 mt-3">
                <FieldWrapper label="Source of cost data" field="costDataSource" />
                <FieldWrapper label="Effective date of cost data" field="costDataDate" type="date" />
                <FieldWrapper label="Quality rating from cost service" field="qualityRating" />
              </div>

              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">ESTIMATED REPRODUCTION OR REPLACEMENT COST NEW</span>
                  <span className="text-sm font-semibold text-gray-900">OPINION OF SITE VALUE = $<input type="text" value={formData.opinionSiteValue} onChange={(e) => handleChange('opinionSiteValue', e.target.value)} className="w-32 border-b border-gray-400 text-right" /></span>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <FieldWrapper label="Dwelling Sq. Ft." field="dwellingSquareFeet" type="number" />
                  <FieldWrapper label="@ $ per sq. ft." field="dwellingCostPerSF" />
                  <FieldWrapper label="= $" field="dwellingTotalCost" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <FieldWrapper label="Garage/Carport Sq. Ft." field="garageCarportSF" type="number" />
                  <FieldWrapper label="@ $ per sq. ft." field="garageCarportCostPerSF" />
                  <FieldWrapper label="= $" field="garageCarportTotal" />
                </div>

                <div className="flex items-center justify-between border-t-2 border-gray-400 pt-2">
                  <span className="text-sm font-semibold text-gray-900">Total Estimate of Cost-New</span>
                  <span className="text-sm font-semibold text-gray-900">= $<input type="text" value={formData.totalEstimatedCostNew} onChange={(e) => handleChange('totalEstimatedCostNew', e.target.value)} className="w-32 border-b border-gray-400 text-right" /></span>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-900">Less Depreciation:</label>
                  <div className="grid grid-cols-3 gap-3">
                    <FieldWrapper label="Physical $" field="physicalDepreciation" />
                    <FieldWrapper label="Functional $" field="functionalDepreciation" />
                    <FieldWrapper label="External $" field="externalDepreciation" />
                  </div>
                </div>

                <div className="flex items-center justify-between border-t-2 border-gray-400 pt-2">
                  <span className="text-sm font-semibold text-gray-900">Depreciated Cost of Improvements</span>
                  <span className="text-sm font-semibold text-gray-900">= $<input type="text" value={formData.depreciatedCostImprovements} onChange={(e) => handleChange('depreciatedCostImprovements', e.target.value)} className="w-32 border-b border-gray-400 text-right" /></span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">"As-is" Value of Site Improvements</span>
                  <span className="text-sm font-semibold text-gray-900">= $<input type="text" value={formData.asIsValueSiteImprovements} onChange={(e) => handleChange('asIsValueSiteImprovements', e.target.value)} className="w-32 border-b border-gray-400 text-right" /></span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <FieldWrapper label="Estimated Remaining Economic Life (HUD and VA only) Years" field="estimatedRemainingLife" type="number" />
                  <div className="flex items-center">
                    <span className="text-sm font-semibold mr-2">Indicated Value By Cost Approach =</span>
                    <span className="text-lg font-bold text-green-700">$<input type="text" value={formData.indicatedValueCost} onChange={(e) => handleChange('indicatedValueCost', e.target.value)} className="w-32 border-b-2 border-green-600 text-right" /></span>
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <FieldWrapper label="Comments on Cost Approach (gross living area calculations, depreciation, etc.)" field="costApproachComments" type="textarea" />
              </div>
            </div>

            {/* INCOME APPROACH */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">INCOME APPROACH TO VALUE (not required by Fannie Mae)</h3>
              <div className="flex items-center space-x-4 mb-3">
                <FieldWrapper label="Estimated Monthly Market Rent $" field="estimatedMonthlyRent" />
                <span className="text-sm text-gray-900">X</span>
                <FieldWrapper label="Gross Rent Multiplier" field="grossRentMultiplier" />
                <span className="text-sm font-semibold text-gray-900">= Indicated Value by Income Approach $<input type="text" value={formData.indicatedValueIncome} onChange={(e) => handleChange('indicatedValueIncome', e.target.value)} className="w-32 border-b-2 border-gray-400" /></span>
              </div>
              <FieldWrapper label="Summary of Income Approach (including support for market rent and GRM)" field="incomeApproachSummary" type="textarea" />
            </div>

            {/* PUD INFORMATION */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">PROJECT INFORMATION FOR PUDs (if applicable)</h3>

              <div className="flex items-center space-x-4 mb-3">
                <span className="text-sm text-gray-900">Is the developer/builder in control of the Homeowners' Association (HOA)?</span>
                <label className="text-gray-900"><input type="radio" checked={formData.pudDeveloperControl === true} onChange={() => handleChange('pudDeveloperControl', true)} className="mr-1" /> Yes</label>
                <label className="text-gray-900"><input type="radio" checked={formData.pudDeveloperControl === false} onChange={() => handleChange('pudDeveloperControl', false)} className="mr-1" /> No</label>
              </div>

              <div className="flex items-center space-x-4 mb-3">
                <span className="text-sm text-gray-900">Unit type(s)</span>
                <label className="text-gray-900"><input type="radio" checked={formData.pudUnitType === 'Detached'} onChange={() => handleChange('pudUnitType', 'Detached')} className="mr-1" /> Detached</label>
                <label className="text-gray-900"><input type="radio" checked={formData.pudUnitType === 'Attached'} onChange={() => handleChange('pudUnitType', 'Attached')} className="mr-1" /> Attached</label>
              </div>

              <p className="text-xs mb-3 italic">Provide the following information for PUDs ONLY if the developer/builder is in control of the HOA and the subject property is an attached dwelling unit.</p>

              <FieldWrapper label="Legal name of project" field="pudProjectName" />

              <div className="grid grid-cols-4 gap-3 mt-3">
                <FieldWrapper label="Total number of phases" field="pudTotalPhases" type="number" />
                <FieldWrapper label="Total number of units" field="pudTotalUnits" type="number" />
                <FieldWrapper label="Total number of units sold" field="pudUnitsSold" type="number" />
                <FieldWrapper label="Total number of units rented" field="pudUnitsRented" type="number" />
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <FieldWrapper label="Total number of units for sale" field="pudUnitsForSale" type="number" />
                <FieldWrapper label="Data source(s)" field="pudDataSource" />
              </div>

              <div className="flex items-center space-x-4 mt-3 mb-3">
                <span className="text-sm text-gray-900">Was the project created by the conversion of an existing building(s) into a PUD?</span>
                <label className="text-gray-900"><input type="radio" checked={formData.pudConversion === true} onChange={() => handleChange('pudConversion', true)} className="mr-1" /> Yes</label>
                <label className="text-gray-900"><input type="radio" checked={formData.pudConversion === false} onChange={() => handleChange('pudConversion', false)} className="mr-1" /> No</label>
                {formData.pudConversion && (
                  <FieldWrapper label="If Yes, date of conversion" field="pudConversionDate" type="date" />
                )}
              </div>

              <div className="flex items-center space-x-4 mb-3">
                <span className="text-sm text-gray-900">Does the project contain any multi-dwelling units?</span>
                <label className="text-gray-900"><input type="radio" checked={formData.pudMultiDwelling === true} onChange={() => handleChange('pudMultiDwelling', true)} className="mr-1" /> Yes</label>
                <label className="text-gray-900"><input type="radio" checked={formData.pudMultiDwelling === false} onChange={() => handleChange('pudMultiDwelling', false)} className="mr-1" /> No</label>
              </div>

              <div className="flex items-center space-x-4 mb-3">
                <span className="text-sm text-gray-900">Are the units, common elements, and recreation facilities complete?</span>
                <label className="text-gray-900"><input type="radio" checked={formData.pudComplete === true} onChange={() => handleChange('pudComplete', true)} className="mr-1" /> Yes</label>
                <label className="text-gray-900"><input type="radio" checked={formData.pudComplete === false} onChange={() => handleChange('pudComplete', false)} className="mr-1" /> No</label>
                {!formData.pudComplete && (
                  <FieldWrapper label="If No, describe the status of completion" field="pudCompletionStatus" type="textarea" />
                )}
              </div>

              <div className="flex items-center space-x-4 mb-3">
                <span className="text-sm text-gray-900">Are the common elements leased to or by the Homeowners' Association?</span>
                <label className="text-gray-900"><input type="radio" checked={formData.pudCommonElementsLeased === true} onChange={() => handleChange('pudCommonElementsLeased', true)} className="mr-1" /> Yes</label>
                <label className="text-gray-900"><input type="radio" checked={formData.pudCommonElementsLeased === false} onChange={() => handleChange('pudCommonElementsLeased', false)} className="mr-1" /> No</label>
                {formData.pudCommonElementsLeased && (
                  <FieldWrapper label="If Yes, describe the rental terms and options" field="pudRentalTerms" type="textarea" />
                )}
              </div>

              <FieldWrapper label="Describe common elements and recreational facilities" field="pudCommonElements" type="textarea" />
            </div>

          </div>
        )}
      </div>

      {/* ==================== PAGES 4-6: CERTIFICATIONS ==================== */}
      <CertificationPages
        formData={formData}
        handleChange={handleChange}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        Field={FieldWrapper}
        CheckboxField={CheckboxFieldWrapper}
        SectionHeader={SectionHeader}
      />

    </div>
  );
};

// Comparables Sales Grid Component
const ComparablesSalesGrid = ({ subjectProperty, formData, selectedComparables, handleChange }) => {
  if (!selectedComparables || selectedComparables.length === 0) {
    return (
      <div className="border-2 border-gray-800 p-8 text-center bg-gray-50">
        <p className="text-gray-500 italic">No comparables selected. Please select comparable properties to display the sales comparison grid.</p>
      </div>
    );
  }

  // ENHANCED: Calculate adjustments using smart adjustment values
  const calculateAdjustment = (feature, comp, subject) => {
    if (!formData.adjustmentValues) return 0;

    const compData = comp.rawData || comp;
    const adj = formData.adjustmentValues;
    let adjustment = 0;

    switch(feature) {
      case 'Site':
        // Lot size adjustment
        const subjectLot = parseFloat(formData.area || subject?.LotSizeSquareFeet || 0);
        const compLot = parseFloat(compData.LotSizeSquareFeet || 0);
        adjustment = (subjectLot - compLot) * (adj.lotSizePerSqFt || 0);
        break;

      case 'Above Grade Room Count / Gross Living Area':
        // GLA adjustment
        const subjectGLA = parseFloat(formData.squareFeetGLA || subject?.LivingArea || 0);
        const compGLA = parseFloat(compData.LivingArea || 0);
        adjustment = (subjectGLA - compGLA) * (adj.glaPerSqFt || 0);
        break;

      case 'Garage/Carport':
        // Garage spaces adjustment
        const subjectGarage = parseFloat(formData.garageSpaces || subject?.GarageSpaces || 0);
        const compGarage = parseFloat(compData.GarageSpaces || 0);
        adjustment = (subjectGarage - compGarage) * (adj.garageSpaceValue || 0);
        break;

      // Add more cases based on the 8 smart adjustment fields
      default:
        adjustment = 0;
    }

    return Math.round(adjustment);
  };

  // ENHANCED: Calculate net adjustments per comparable
  const calculateNetAdjustment = (compIndex) => {
    const comp = selectedComparables[compIndex];
    const features = ['Site', 'Above Grade Room Count / Gross Living Area', 'Garage/Carport'];

    let total = 0;
    features.forEach(feature => {
      total += calculateAdjustment(feature, comp, subjectProperty);
    });

    return total;
  };

  // ENHANCED: Calculate adjusted sale price
  const calculateAdjustedPrice = (compIndex) => {
    const comp = selectedComparables[compIndex];
    const compData = comp.rawData || comp;
    const salePrice = compData.ClosePrice || compData.ListPrice || 0;
    const netAdjustment = calculateNetAdjustment(compIndex);
    return salePrice + netAdjustment;
  };

  const adjustmentFields = [
    'Sale or Financing Concessions',
    'Date of Sale/Time',
    'Location',
    'Leasehold/Fee Simple',
    'Site',
    'View',
    'Design (Style)',
    'Quality of Construction',
    'Actual Age',
    'Condition',
    'Above Grade Room Count / Gross Living Area',
    'Basement & Finished Rooms Below Grade',
    'Functional Utility',
    'Heating/Cooling',
    'Energy Efficient Items',
    'Garage/Carport',
    'Porch/Patio/Deck'
  ];

  return (
    <div className="border-2 border-gray-800 overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-800 text-white">
            <th className="border border-gray-400 p-2 text-left sticky left-0 bg-gray-800 z-10">FEATURE</th>
            <th className="border border-gray-400 p-2">SUBJECT</th>
            {selectedComparables.map((comp, idx) => (
              <th key={idx} className="border border-gray-400 p-2">COMPARABLE SALE # {idx + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {/* Address */}
          <tr>
            <td className="border border-gray-400 p-2 font-semibold bg-gray-100 sticky left-0 z-10 text-gray-900">Address</td>
            <td className="border border-gray-400 p-2 text-gray-900">{formData.propertyAddress || subjectProperty?.UnparsedAddress || ''}</td>
            {selectedComparables.map((comp, idx) => (
              <td key={idx} className="border border-gray-400 p-2 text-gray-900">{comp.rawData?.UnparsedAddress || comp.address || ''}</td>
            ))}
          </tr>

          {/* Proximity to Subject */}
          <tr>
            <td className="border border-gray-400 p-2 font-semibold bg-gray-100 sticky left-0 z-10 text-gray-900">Proximity to Subject</td>
            <td className="border border-gray-400 p-2 text-gray-900">-</td>
            {selectedComparables.map((comp, idx) => (
              <td key={idx} className="border border-gray-400 p-2 text-gray-900">{comp.distance ? `${parseFloat(comp.distance).toFixed(2)} mi` : ''}</td>
            ))}
          </tr>

          {/* Sale Price */}
          <tr>
            <td className="border border-gray-400 p-2 font-semibold bg-gray-100 sticky left-0 z-10 text-gray-900">Sale Price</td>
            <td className="border border-gray-400 p-2 font-bold text-gray-900">${formData.contractPrice || subjectProperty?.ListPrice || 0}</td>
            {selectedComparables.map((comp, idx) => (
              <td key={idx} className="border border-gray-400 p-2 font-bold text-gray-900">${comp.rawData?.ClosePrice || comp.rawData?.ListPrice || 0}</td>
            ))}
          </tr>

          {/* Sale Price/Gross Liv. Area */}
          <tr>
            <td className="border border-gray-400 p-2 font-semibold bg-gray-100 sticky left-0 z-10 text-gray-900">Sale Price/Gross Liv. Area</td>
            <td className="border border-gray-400 p-2 text-gray-900">${((formData.contractPrice || 0) / (formData.squareFeetGLA || 1)).toFixed(2)} sq. ft.</td>
            {selectedComparables.map((comp, idx) => {
              const price = comp.rawData?.ClosePrice || comp.rawData?.ListPrice || 0;
              const sqft = comp.rawData?.LivingArea || 1;
              return <td key={idx} className="border border-gray-400 p-2 text-gray-900">${(price / sqft).toFixed(2)} sq. ft.</td>;
            })}
          </tr>

          {/* Data Source(s) */}
          <tr>
            <td className="border border-gray-400 p-2 font-semibold bg-gray-100 sticky left-0 z-10 text-gray-900">Data Source(s)</td>
            <td className="border border-gray-400 p-2 text-gray-900"></td>
            {selectedComparables.map((comp, idx) => (
              <td key={idx} className="border border-gray-400 p-2 text-gray-900">{comp.rawData?.DataSourceName || 'MLS'}</td>
            ))}
          </tr>

          {/* Verification Source(s) */}
          <tr>
            <td className="border border-gray-400 p-2 font-semibold bg-gray-100 sticky left-0 z-10 text-gray-900">Verification Source(s)</td>
            <td className="border border-gray-400 p-2 text-gray-900"></td>
            {selectedComparables.map((comp, idx) => (
              <td key={idx} className="border border-gray-400 p-2 text-gray-900">MLS</td>
            ))}
          </tr>

          {/* VALUE ADJUSTMENTS HEADER */}
          <tr className="bg-blue-100">
            <td className="border border-gray-400 p-2 font-bold sticky left-0 z-10 bg-blue-100 text-gray-900">VALUE ADJUSTMENTS</td>
            <td className="border border-gray-400 p-2 font-bold text-gray-900">DESCRIPTION</td>
            {selectedComparables.map((comp, idx) => (
              <td key={idx} className="border border-gray-400 p-2 font-bold text-gray-900">DESCRIPTION + (-) $ Adjustment</td>
            ))}
          </tr>

          {/* Adjustment Rows */}
          {adjustmentFields.map((field, fieldIdx) => (
            <tr key={fieldIdx}>
              <td className="border border-gray-400 p-2 font-semibold bg-gray-50 sticky left-0 z-10 text-gray-900">{field}</td>
              <td className="border border-gray-400 p-2 text-gray-900">
                <input
                  type="text"
                  className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded"
                  placeholder="Description"
                />
              </td>
              {selectedComparables.map((comp, compIdx) => {
                const adjustment = calculateAdjustment(field, comp, subjectProperty);
                return (
                  <td key={compIdx} className="border border-gray-400 p-2 text-gray-900">
                    <div className="flex space-x-1">
                      <input type="text" className="w-1/2 px-1 py-0.5 text-xs border border-gray-300 rounded" placeholder="Desc" />
                      <input
                        type="text"
                        className="w-1/2 px-1 py-0.5 text-xs border border-gray-300 rounded"
                        placeholder="$ Adj"
                        value={adjustment !== 0 ? `$${adjustment.toLocaleString()}` : ''}
                        readOnly
                      />
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}

          {/* Net Adjustment (Total) */}
          <tr className="bg-yellow-50">
            <td className="border border-gray-400 p-2 font-bold sticky left-0 z-10 bg-yellow-50 text-gray-900">Net Adjustment (Total)</td>
            <td className="border border-gray-400 p-2 text-gray-900"></td>
            {selectedComparables.map((comp, idx) => {
              const netAdj = calculateNetAdjustment(idx);
              return (
                <td key={idx} className="border border-gray-400 p-2 text-gray-900">
                  <input
                    type="text"
                    className="w-full px-1 py-0.5 text-xs border border-gray-400 rounded font-bold bg-yellow-50"
                    value={`$${netAdj.toLocaleString()}`}
                    readOnly
                  />
                </td>
              );
            })}
          </tr>

          {/* Adjusted Sale Price of Comparables */}
          <tr className="bg-green-100">
            <td className="border border-gray-400 p-2 font-bold sticky left-0 z-10 bg-green-100 text-gray-900">Adjusted Sale Price of Comparables</td>
            <td className="border border-gray-400 p-2 text-gray-900"></td>
            {selectedComparables.map((comp, idx) => {
              const adjustedPrice = calculateAdjustedPrice(idx);
              return (
                <td key={idx} className="border border-gray-400 p-2 text-gray-900">
                  <input
                    type="text"
                    className="w-full px-1 py-0.5 text-xs border border-gray-400 rounded font-bold bg-green-50"
                    value={`$${adjustedPrice.toLocaleString()}`}
                    readOnly
                  />
                </td>
              );
            })}
          </tr>

          {/* Net Adj. % / Gross Adj. % */}
          <tr>
            <td className="border border-gray-400 p-2 font-semibold bg-gray-100 sticky left-0 z-10 text-gray-900">Net Adj. % / Gross Adj. %</td>
            <td className="border border-gray-400 p-2 text-gray-900"></td>
            {selectedComparables.map((comp, idx) => (
              <td key={idx} className="border border-gray-400 p-2 text-gray-900">
                <div className="flex space-x-1">
                  <input type="text" className="w-1/2 px-1 py-0.5 text-xs border border-gray-300 rounded" placeholder="Net %" />
                  <input type="text" className="w-1/2 px-1 py-0.5 text-xs border border-gray-300 rounded" placeholder="Gross %" />
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Certification Pages Component (Pages 4-6)
const CertificationPages = ({ formData, handleChange, expandedSections, toggleSection, Field, CheckboxField, SectionHeader }) => {
  return (
    <>
      {/* PAGE 4 - SCOPE OF WORK */}
      <div className="border-b-4 border-gray-900">
        <SectionHeader title="PAGE 4: SCOPE OF WORK, INTENDED USE, DEFINITIONS, ASSUMPTIONS" section="page4" bgColor="bg-purple-800" />
        {expandedSections.page4 && (
          <div className="p-6 space-y-4 text-xs text-gray-900">
            <div className="bg-blue-50 border-2 border-blue-300 p-4">
              <p className="font-semibold mb-2 text-gray-900">This report form is designed to report an appraisal of a one-unit property or a one-unit property with an accessory unit; including a unit in a planned unit development (PUD). This report form is not designed to report an appraisal of a manufactured home or a unit in a condominium or cooperative project.</p>
            </div>

            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold mb-2 text-gray-900">SCOPE OF WORK:</h3>
              <p className="text-gray-900">The scope of work for this appraisal is defined by the complexity of this appraisal assignment and the reporting requirements of this appraisal report form, including the following definition of market value, statement of assumptions and limiting conditions, and certifications. The appraiser must, at a minimum: (1) perform a complete visual inspection of the interior and exterior areas of the subject property, (2) inspect the neighborhood, (3) inspect each of the comparable sales from at least the street, (4) research, verify, and analyze data from reliable public and/or private sources, and (5) report his or her analysis, opinions, and conclusions in this appraisal report.</p>
            </div>

            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold mb-2 text-gray-900">INTENDED USE:</h3>
              <p className="text-gray-900">The intended use of this appraisal report is for the lender/client to evaluate the property that is the subject of this appraisal for a mortgage finance transaction.</p>
            </div>

            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold mb-2 text-gray-900">INTENDED USER:</h3>
              <p className="text-gray-900">The intended user of this appraisal report is the lender/client.</p>
            </div>

            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold mb-2 text-gray-900">DEFINITION OF MARKET VALUE:</h3>
              <p className="text-gray-900">The most probable price which a property should bring in a competitive and open market under all conditions requisite to a fair sale, the buyer and seller, each acting prudently, knowledgeably and assuming the price is not affected by undue stimulus. Implicit in this definition is the consummation of a sale as of a specified date and the passing of title from seller to buyer under conditions whereby: (1) buyer and seller are typically motivated; (2) both parties are well informed or well advised, and each acting in what he or she considers his or her own best interest; (3) a reasonable time is allowed for exposure in the open market; (4) payment is made in terms of cash in U. S. dollars or in terms of financial arrangements comparable thereto; and (5) the price represents the normal consideration for the property sold unaffected by special or creative financing or sales concessions* granted by anyone associated with the sale.</p>
              <p className="mt-2 italic text-gray-900">*Adjustments to the comparables must be made for special or creative financing or sales concessions. No adjustments are necessary for those costs which are normally paid by sellers as a result of tradition or law in a market area; these costs are readily identifiable since the seller pays these costs in virtually all sales transactions. Special or creative financing adjustments can be made to the comparable property by comparisons to financing terms offered by a third party institutional lender that is not already involved in the property or transaction. Any adjustment should not be calculated on a mechanical dollar for dollar cost of the financing or concession but the dollar amount of any adjustment should approximate the market's reaction to the financing or concessions based on the appraiser's judgment.</p>
            </div>

            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold mb-2 text-gray-900">STATEMENT OF ASSUMPTIONS AND LIMITING CONDITIONS:</h3>
              <p className="mb-2 text-gray-900">The appraiser's certification in this report is subject to the following assumptions and limiting conditions:</p>
              <ol className="list-decimal list-inside space-y-2 text-gray-900">
                <li>The appraiser will not be responsible for matters of a legal nature that affect either the property being appraised or the title to it, except for information that he or she became aware of during the research involved in performing this appraisal. The appraiser assumes that the title is good and marketable and will not render any opinions about the title.</li>
                <li>The appraiser has provided a sketch in this appraisal report to show the approximate dimensions of the improvements. The sketch is included only to assist the reader in visualizing the property and understanding the appraiser's determination of its size.</li>
                <li>The appraiser has examined the available flood maps that are provided by the Federal Emergency Management Agency (or other data sources) and has noted in this appraisal report whether any portion of the subject site is located in an identified Special Flood Hazard Area. Because the appraiser is not a surveyor, he or she makes no guarantees, express or implied, regarding this determination.</li>
                <li>The appraiser will not give testimony or appear in court because he or she made an appraisal of the property in question, unless specific arrangements to do so have been made beforehand, or as otherwise required by law.</li>
                <li>The appraiser has noted in this appraisal report any adverse conditions (such as needed repairs, deterioration, the presence of hazardous wastes, toxic substances, etc.) observed during the inspection of the subject property or that he or she became aware of during the research involved in performing this appraisal. Unless otherwise stated in this appraisal report, the appraiser has no knowledge of any hidden or unapparent physical deficiencies or adverse conditions of the property (such as, but not limited to, needed repairs, deterioration, the presence of hazardous wastes, toxic substances, adverse environmental conditions, etc.) that would make the property less valuable, and has assumed that there are no such conditions and makes no guarantees or warranties, express or implied. The appraiser will not be responsible for any such conditions that do exist or for any engineering or testing that might be required to discover whether such conditions exist. Because the appraiser is not an expert in the field of environmental hazards, this appraisal report must not be considered as an environmental assessment of the property.</li>
                <li>The appraiser has based his or her appraisal report and valuation conclusion for an appraisal that is subject to satisfactory completion, repairs, or alterations on the assumption that the completion, repairs, or alterations of the subject property will be performed in a professional manner.</li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* PAGE 5 - APPRAISER'S CERTIFICATION */}
      <div className="border-b-4 border-gray-900">
        <SectionHeader title="PAGE 5: APPRAISER'S CERTIFICATION" section="page5" bgColor="bg-red-800" />
        {expandedSections.page5 && (
          <div className="p-6 space-y-4 text-xs text-gray-900">
            <div className="bg-red-50 border-2 border-red-300 p-4">
              <h3 className="font-bold text-sm mb-2 text-gray-900">APPRAISER'S CERTIFICATION:</h3>
              <p className="mb-2 text-gray-900">The Appraiser certifies and agrees that:</p>
              <ol className="list-decimal list-inside space-y-2 text-gray-900">
                <li>I have, at a minimum, developed and reported this appraisal in accordance with the scope of work requirements stated in this appraisal report.</li>
                <li>I performed a complete visual inspection of the interior and exterior areas of the subject property. I reported the condition of the improvements in factual, specific terms. I identified and reported the physical deficiencies that could affect the livability, soundness, or structural integrity of the property.</li>
                <li>I performed this appraisal in accordance with the requirements of the Uniform Standards of Professional Appraisal Practice that were adopted and promulgated by the Appraisal Standards Board of The Appraisal Foundation and that were in place at the time this appraisal report was prepared.</li>
                <li>I developed my opinion of the market value of the real property that is the subject of this report based on the sales comparison approach to value. I have adequate comparable market data to develop a reliable sales comparison approach for this appraisal assignment. I further certify that I considered the cost and income approaches to value but did not develop them, unless otherwise indicated in this report.</li>
                <li>I researched, verified, analyzed, and reported on any current agreement for sale for the subject property, any offering for sale of the subject property in the twelve months prior to the effective date of this appraisal, and the prior sales of the subject property for a minimum of three years prior to the effective date of this appraisal, unless otherwise indicated in this report.</li>
                <li>I researched, verified, analyzed, and reported on the prior sales of the comparable sales for a minimum of one year prior to the date of sale of the comparable sale, unless otherwise indicated in this report.</li>
                <li>I selected and used comparable sales that are locationally, physically, and functionally the most similar to the subject property.</li>
                <li>I have not used comparable sales that were the result of combining a land sale with the contract purchase price of a home that has been built or will be built on the land.</li>
                <li>I have reported adjustments to the comparable sales that reflect the market's reaction to the differences between the subject property and the comparable sales.</li>
                <li>I verified, from a disinterested source, all information in this report that was provided by parties who have a financial interest in the sale or financing of the subject property.</li>
                <li>I have knowledge and experience in appraising this type of property in this market area.</li>
                <li>I am aware of, and have access to, the necessary and appropriate public and private data sources, such as multiple listing services, tax assessment records, public land records and other such data sources for the area in which the property is located.</li>
                <li>I obtained the information, estimates, and opinions furnished by other parties and expressed in this appraisal report from reliable sources that I believe to be true and correct.</li>
                <li>I have taken into consideration the factors that have an impact on value with respect to the subject neighborhood, subject property, and the proximity of the subject property to adverse influences in the development of my opinion of market value. I have noted in this appraisal report any adverse conditions (such as, but not limited to, needed repairs, deterioration, the presence of hazardous wastes, toxic substances, adverse environmental conditions, etc.) observed during the inspection of the subject property or that I became aware of during the research involved in performing this appraisal. I have considered these adverse conditions in my analysis of the property value, and have reported on the effect of the conditions on the value and marketability of the subject property.</li>
                <li>I have not knowingly withheld any significant information from this appraisal report and, to the best of my knowledge, all statements and information in this appraisal report are true and correct.</li>
                <li>I stated in this appraisal report my own personal, unbiased, and professional analysis, opinions, and conclusions, which are subject only to the assumptions and limiting conditions in this appraisal report.</li>
                <li>I have no present or prospective interest in the property that is the subject of this report, and I have no present or prospective personal interest or bias with respect to the participants in the transaction. I did not base, either partially or completely, my analysis and/or opinion of market value in this appraisal report on the race, color, religion, sex, age, marital status, handicap, familial status, or national origin of either the prospective owners or occupants of the subject property or of the present owners or occupants of the properties in the vicinity of the subject property or on any other basis prohibited by law.</li>
                <li>My employment and/or compensation for performing this appraisal or any future or anticipated appraisals was not conditioned on any agreement or understanding, written or otherwise, that I would report (or present analysis supporting) a predetermined specific value, a predetermined minimum value, a range or direction in value, a value that favors the cause of any party, or the attainment of a specific result or occurrence of a specific subsequent event (such as approval of a pending mortgage loan application).</li>
                <li>I personally prepared all conclusions and opinions about the real estate that were set forth in this appraisal report. If I relied on significant real property appraisal assistance from any individual or individuals in the performance of this appraisal or the preparation of this appraisal report, I have named such individual(s) and disclosed the specific tasks performed in this appraisal report. I certify that any individual so named is qualified to perform the tasks. I have not authorized anyone to make a change to any item in this appraisal report; therefore, any change made to this appraisal is unauthorized and I will take no responsibility for it.</li>
                <li>I identified the lender/client in this appraisal report who is the individual, organization, or agent for the organization that ordered and will receive this appraisal report.</li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* PAGE 6 - SIGNATURES & DISCLOSURES */}
      <div className="border-b-4 border-gray-900">
        <SectionHeader title="PAGE 6: APPRAISER INFORMATION & SIGNATURES" section="page6" bgColor="bg-indigo-800" />
        {expandedSections.page6 && (
          <div className="p-6 space-y-6">

            {/* APPRAISER */}
            <div className="border-2 border-gray-800 p-4 bg-blue-50">
              <h3 className="font-bold text-sm mb-3 bg-blue-200 px-2 py-1 text-gray-900">APPRAISER</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Signature" field="appraiserSignature" placeholder="(Sign when complete)" />
                <Field label="Name" field="appraiserName" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Company Name" field="appraiserCompany" />
                <Field label="Company Address" field="appraiserAddress" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Telephone Number" field="appraiserPhone" />
                <Field label="Email Address" field="appraiserEmail" type="email" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Date of Signature and Report" field="signatureDate" type="date" />
                <Field label="Effective Date of Appraisal" field="effectiveDate" type="date" />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <Field label="State Certification #" field="stateCertification" />
                <Field label="State" field="appraiserState" />
                <Field label="Expiration Date of Certification or License" field="certificationExpiration" type="date" />
              </div>
            </div>

            {/* SUBJECT PROPERTY INSPECTION */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 text-gray-900">SUBJECT PROPERTY</h3>
              <div className="space-y-2">
                <label className="flex items-center text-sm text-gray-900">
                  <input type="radio" checked={!formData.inspectedSubjectProperty} onChange={() => handleChange('inspectedSubjectProperty', false)} className="mr-2" />
                  Did not inspect subject property
                </label>
                <label className="flex items-center text-sm text-gray-900">
                  <input type="radio" checked={formData.inspectedExteriorOnly} onChange={() => handleChange('inspectedExteriorOnly', true)} className="mr-2" />
                  Did inspect exterior of subject property from street
                </label>
                {formData.inspectedExteriorOnly && <Field label="Date of Inspection" field="inspectionDate" type="date" />}
                <label className="flex items-center text-sm text-gray-900">
                  <input type="radio" checked={formData.inspectedInteriorExterior} onChange={() => handleChange('inspectedInteriorExterior', true)} className="mr-2" />
                  Did inspect interior and exterior of subject property
                </label>
                {formData.inspectedInteriorExterior && <Field label="Date of Inspection" field="inspectionDate" type="date" />}
              </div>
            </div>

            {/* COMPARABLE SALES INSPECTION */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 text-gray-900">COMPARABLE SALES</h3>
              <div className="space-y-2">
                <label className="flex items-center text-sm text-gray-900">
                  <input type="radio" checked={!formData.inspectedComparables} onChange={() => handleChange('inspectedComparables', false)} className="mr-2" />
                  Did not inspect exterior of comparable sales from street
                </label>
                <label className="flex items-center text-sm text-gray-900">
                  <input type="radio" checked={formData.inspectedComparables} onChange={() => handleChange('inspectedComparables', true)} className="mr-2" />
                  Did inspect exterior of comparable sales from street
                </label>
                {formData.inspectedComparables && <Field label="Date of Inspection" field="comparableInspectionDate" type="date" />}
              </div>
            </div>

            {/* SUPERVISORY APPRAISER (if required) */}
            <div className="border-2 border-gray-800 p-4 bg-orange-50">
              <div className="flex items-center space-x-4 mb-3">
                <CheckboxField label="Supervisory Appraiser (ONLY IF REQUIRED)" field="supervisoryAppraiser" />
              </div>
              {formData.supervisoryAppraiser && (
                <>
                  <h3 className="font-bold text-sm mb-3 bg-orange-200 px-2 py-1 text-gray-900">SUPERVISORY APPRAISER</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Signature" field="supervisorSignature" placeholder="(Sign when complete)" />
                    <Field label="Name" field="supervisorName" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Field label="Company Name" field="supervisorCompany" />
                    <Field label="Company Address" field="supervisorAddress" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Field label="Telephone Number" field="supervisorPhone" />
                    <Field label="Email Address" field="supervisorEmail" type="email" />
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <Field label="Date of Signature" field="supervisorSignatureDate" type="date" />
                    <Field label="State Certification #" field="supervisorCertification" />
                    <Field label="State" field="supervisorState" />
                  </div>
                  <div className="mt-3">
                    <Field label="Expiration Date of Certification or License" field="supervisorExpiration" type="date" />
                  </div>
                </>
              )}
            </div>

            {/* LENDER/CLIENT INFO */}
            <div className="border-2 border-gray-800 p-4">
              <h3 className="font-bold text-sm mb-3 bg-gray-200 px-2 py-1 text-gray-900">LENDER/CLIENT</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name" field="lenderClient" />
                <Field label="Company Name" field="lenderCompany" />
              </div>
              <div className="mt-3">
                <Field label="Company Address" field="lenderAddress" />
              </div>
              <div className="mt-3">
                <Field label="Email Address" field="lenderEmail" type="email" />
              </div>
            </div>

            {/* FINAL PROPERTY & VALUE INFO */}
            <div className="border-4 border-green-600 bg-green-50 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1 text-gray-900">ADDRESS OF PROPERTY APPRAISED</label>
                  <div className="text-sm font-semibold text-gray-900">{formData.propertyAddress}</div>
                  <div className="text-sm text-gray-900">{formData.city}, {formData.state} {formData.zip}</div>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1 text-gray-900">APPRAISED VALUE OF SUBJECT PROPERTY</label>
                  <div className="text-2xl font-bold text-green-700">${formData.marketValue || '0'}</div>
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </>
  );
};

export default UAD26FormComplete;
